import type { PortfolioContractType, PortfolioRow } from "@chro/shared";

export interface PortfolioTimelinePreferences {
  mode: "net" | "all";
  units: "mw" | "both";
  // Shared portfolio scope; retain these keys for existing saved preferences.
  book: string | null;
  trader: string | null;
}
export const defaultTimelinePreferences: PortfolioTimelinePreferences = { mode: "net", units: "mw", book: null, trader: null };
export interface PortfolioPreferences {
  commodity: "all" | "power" | "gas";
  types: PortfolioContractType[];
  separate: boolean;
  volume: "remaining" | "full";
  basis: "entry" | "market";
  order: "period" | "product";
  breakdown: "none" | "book" | "trader" | "both";
  timeline: PortfolioTimelinePreferences;
}
export const defaultPortfolioPreferences: PortfolioPreferences = {
  commodity: "all", types: ["cleared"], separate: true, volume: "remaining", basis: "entry", order: "period", breakdown: "none", timeline: defaultTimelinePreferences,
};
export function parsePortfolioPreferences(value: unknown): PortfolioPreferences {
  const p = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const one = <T extends string>(key: string, choices: T[], fallback: T): T => choices.includes(p[key] as T) ? p[key] as T : fallback;
  const t = p.timeline && typeof p.timeline === "object" ? p.timeline as Record<string, unknown> : {};
  return { commodity: one("commodity", ["all", "power", "gas"], "all"),
    types: Array.isArray(p.types) ? [...new Set(p.types.filter((t): t is PortfolioContractType => ["cleared", "otc_financial", "physical"].includes(t)))] : ["cleared"],
    separate: typeof p.separate === "boolean" ? p.separate : true,
    volume: one("volume", ["remaining", "full"], "remaining"), basis: one("basis", ["entry", "market"], "entry"),
    order: one("order", ["period", "product"], "period"), breakdown: one("breakdown", ["none", "book", "trader", "both"], "none"),
    timeline: { mode: t.mode === "all" ? "all" : "net", units: t.units === "both" ? "both" : "mw",
      book: typeof t.book === "string" ? t.book : null, trader: typeof t.trader === "string" ? t.trader : null } };
}
export function selectPortfolioScopeRows(rows: PortfolioRow[], scope: Pick<PortfolioTimelinePreferences, "book" | "trader">): PortfolioRow[] {
  return rows.filter(row => (scope.book === null || (row.book ?? "") === scope.book) && (scope.trader === null || (row.trader ?? "") === scope.trader));
}
export function portfolioScopeOptions(rows: PortfolioRow[], field: "book" | "trader", selected: string | null, labels: string[] = []): string[] {
  return [...new Set([...rows.map(row => row[field] ?? ""), ...labels, ...(selected === null ? [] : [selected])])].sort((a, b) => a.localeCompare(b));
}
// MW are averaged over each product's delivery hours. Unlike MWh, averages
// over different months, commodities or base/peak calendars cannot be added.
export function comparableMonthlyMW(rows: PortfolioRow[], volume: PortfolioPreferences["volume"]): boolean {
  if (new Set(rows.map(row => `${row.commodity}|${row.exposureMonth}`)).size > 1) return false;
  const hours = rows.flatMap(row => {
    const measure = row[volume];
    return measure.mwh != null && measure.averageMW != null && measure.averageMW > 0 ? [measure.mwh / measure.averageMW] : [];
  });
  return hours.length < 2 || Math.max(...hours) - Math.min(...hours) < 0.01;
}
export function portfolioSourceGroups(rows: PortfolioRow[]): { key: string; rows: PortfolioRow[] }[] {
  const groups = new Map<string, PortfolioRow[]>();
  for (const row of rows) {
    const key = JSON.stringify([row.commodity, row.product, row.exposureMonth, row.instrumentPeriod, row.instrumentDeliveryStart, row.instrumentDeliveryEnd]);
    const group = groups.get(key) ?? [];
    group.push(row); groups.set(key, group);
  }
  return [...groups].map(([key, items]) => ({ key, rows: items }));
}
export function selectPortfolioRows(rows: PortfolioRow[], prefs: PortfolioPreferences): PortfolioRow[] {
  return rows.filter(r => prefs.types.includes(r.contractType) && (prefs.commodity === "all" || r.commodity === prefs.commodity));
}
export interface PortfolioMetric { value: number | null; missing: number; present: number }
export interface PortfolioSubtotal extends PortfolioMetric { value: number }
export interface PortfolioTotals {
  longMW: PortfolioSubtotal; shortMW: PortfolioSubtotal; netMW: PortfolioSubtotal;
  longMWh: PortfolioSubtotal; shortMWh: PortfolioSubtotal; netMWh: PortfolioSubtotal; notional: PortfolioSubtotal;
  longNotional: PortfolioSubtotal; shortNotional: PortfolioSubtotal; netNotional: PortfolioMetric;
}
export function portfolioTotals(rows: PortfolioRow[], prefs: Pick<PortfolioPreferences, "volume" | "basis">): PortfolioTotals {
  const sum = (get: (r: PortfolioRow) => number | null, items = rows): PortfolioSubtotal => items.reduce((a, r) => {
    const value = get(r);
    if (value == null || !Number.isFinite(value)) a.missing++; else { a.value += value; a.present++; }
    return a;
  }, { value: 0, missing: 0, present: 0 });
  const sideValue = (r: PortfolioRow, field: "MW" | "MWh", side: "B" | "S" | "net") => {
    if (r.side == null) return null;
    if (side !== "net" && r.side !== side) return 0;
    const quantity = field === "MW" ? r[prefs.volume].averageMW : r[prefs.volume].mwh;
    return quantity == null ? null : quantity * (side === "net" && r.side === "S" ? -1 : 1);
  };
  // The API supplies absolute EUR notionals, including for negative energy prices.
  const notional = (r: PortfolioRow) => r[prefs.volume][prefs.basis === "entry" ? "entryNotionalEUR" : "marketNotionalEUR"];
  const sideNotional = (side: "B" | "S") => sum(r => r.side == null ? null : notional(r), rows.filter(r => r.side === side || r.side == null));
  const netNotional = sum(r => { const value = notional(r); return r.side == null || value == null ? null : (r.side === "S" ? -value : value); });
  return { longMW: sum(r => sideValue(r, "MW", "B")), shortMW: sum(r => sideValue(r, "MW", "S")), netMW: sum(r => sideValue(r, "MW", "net")),
    longMWh: sum(r => sideValue(r, "MWh", "B")), shortMWh: sum(r => sideValue(r, "MWh", "S")), netMWh: sum(r => sideValue(r, "MWh", "net")),
    longNotional: sideNotional("B"), shortNotional: sideNotional("S"),
    // A signed subtotal can imply the opposite direction from the complete
    // portfolio. Keep gross available subtotals, but never publish a partial net.
    netNotional: { ...netNotional, value: netNotional.missing ? null : netNotional.value },
    notional: sum(notional) };
}
export const commodityLabels = { power: "Power", gas: "Gas", unknown: "Unmapped" };
export const portfolioTypeLabels: Record<PortfolioContractType, string> = { cleared: "ECC-cleared", otc_financial: "OTC financial", physical: "Physical", unclassified: "Unclassified" };
// Display labels only: grouping identities use the product and exposure month.
export function portfolioProductLabel(row: Pick<PortfolioRow, "product" | "commodity">): string {
  if (row.commodity === "power") return row.product.replace(/\bBase\b/g, "Baseload").replace(/\bPeak\b/g, "Peakload");
  return row.commodity === "gas" && /^(TTF|CEGH|NBP)$/.test(row.product) ? `${row.product} Natural Gas` : row.product;
}
export function portfolioPeriodLabel(row: Pick<PortfolioRow, "period" | "deliveryStart" | "deliveryEnd">): string {
  const start = new Date(`${row.deliveryStart}T00:00:00Z`);
  if (!Number.isFinite(start.getTime()) || !/^\d{4}-\d{2}-\d{2}$/.test(row.deliveryEnd)) return row.period;
  const year = start.getUTCFullYear(), month = start.getUTCMonth();
  const endOf = (months: number) => new Date(Date.UTC(year, month + months, 0)).toISOString().slice(0, 10);
  if (start.getUTCDate() === 1) {
    if (row.deliveryEnd === endOf(1)) return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }).format(start);
    if (month % 3 === 0 && row.deliveryEnd === endOf(3)) return `Q${month / 3 + 1} ${year}`;
    if (month === 0 && row.deliveryEnd === endOf(12)) return `Cal ${year}`;
  }
  return row.period;
}
export function portfolioMonthLabel(month: string): string {
  return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T00:00:00Z`));
}
export function portfolioInstrumentLabel(row: PortfolioRow): string {
  return portfolioPeriodLabel({ period: row.instrumentPeriod, deliveryStart: row.instrumentDeliveryStart, deliveryEnd: row.instrumentDeliveryEnd });
}
type Dimension = "commodity" | "product" | "period" | "book" | "trader";
export interface PortfolioGroup { key: string; label: string; dimension: Dimension; rows: PortfolioRow[]; children: PortfolioGroup[] }
export function groupPortfolio(rows: PortfolioRow[], p: PortfolioPreferences): PortfolioGroup[] {
  const dimensions: Dimension[] = [
    ...(p.separate && p.commodity === "all" ? ["commodity" as const] : []),
    ...(p.order === "period" ? ["period", "product"] as const : ["product", "period"] as const),
    ...(p.breakdown === "book" || p.breakdown === "both" ? ["book" as const] : []),
    ...(p.breakdown === "trader" || p.breakdown === "both" ? ["trader" as const] : []),
  ];
  const walk = (items: PortfolioRow[], level: number, parent: string): PortfolioGroup[] => {
    const dimension = dimensions[level]; if (!dimension) return [];
    const groups = new Map<string, PortfolioGroup>();
    for (const r of items) {
      const identity = dimension === "period" ? r.exposureMonth : dimension === "product" ? `${r.commodity}|${r.product}` : r[dimension];
      const key = JSON.stringify([parent, dimension, identity]);
      const label = dimension === "commodity" ? commodityLabels[r.commodity] : r[dimension] ?? "Unassigned";
      if (!groups.has(key)) groups.set(key, { key, label, dimension, rows: [], children: [] });
      groups.get(key)!.rows.push(r);
    }
    const result = [...groups.values()].sort((a, b) => dimension === "period"
      ? a.rows[0]!.exposureMonth.localeCompare(b.rows[0]!.exposureMonth)
      : dimension === "commodity" ? ["Power", "Gas", "Unmapped"].indexOf(a.label) - ["Power", "Gas", "Unmapped"].indexOf(b.label)
      : a.label.localeCompare(b.label));
    for (const group of result) group.children = walk(group.rows, level + 1, group.key);
    return result;
  };
  return walk(rows, 0, "portfolio");
}
